import { NgTemplateOutlet } from '@angular/common';
import type { ElementRef, TemplateRef } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    afterNextRender,
    computed,
    contentChild,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { NgIcon } from '@ng-icons/core';
import { RouterLink } from '@angular/router';
import { QpxButton, QpxIcon } from '../../directives';
import type {
    QpxOverflowActionItem,
    QpxOverflowInlineItemTemplateContext,
    QpxOverflowMenuItemTemplateContext,
    QpxOverflowTriggerTemplateContext,
} from './models';
import { QpxOverflowInlineItemTemplate } from './overflow-inline-item-template';
import { QpxOverflowMenuItemTemplate } from './overflow-menu-item-template';
import { QpxOverflowTriggerTemplate } from './overflow-trigger-template';

function hasPinnedPriority(item: QpxOverflowActionItem): boolean {
    return item.priority === 'pinned';
}

@Component({
    selector: 'qpx-overflow-actions',
    imports: [
        NgIcon,
        NgTemplateOutlet,
        MatButtonModule,
        MatMenuModule,
        RouterLink,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './overflow-actions.html',
    styleUrl: './overflow-actions.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block min-w-0 w-full',
    },
})
export class QpxOverflowActions {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region Inputs
    public readonly items = input.required<readonly QpxOverflowActionItem[]>();

    public readonly menuAriaLabel = input('More actions');

    public readonly triggerAriaLabel = input('Open more actions');

    public readonly menuXPosition = input<'before' | 'after'>('before');

    public readonly triggerVariant = input<'icon' | 'outlined'>('icon');
    //#endregion

    //#region Template references
    private readonly containerRef =
        viewChild.required<ElementRef<HTMLElement>>('container');

    private readonly measurementRowRef =
        viewChild.required<ElementRef<HTMLElement>>('measurementRow');

    private readonly menuTrigger = viewChild(MatMenuTrigger);

    private readonly defaultInlineItemTemplate = viewChild.required<
        TemplateRef<QpxOverflowInlineItemTemplateContext>
    >('defaultInlineItemTemplate');

    private readonly defaultMenuItemTemplate = viewChild.required<
        TemplateRef<QpxOverflowMenuItemTemplateContext>
    >('defaultMenuItemTemplate');

    private readonly defaultTriggerTemplate = viewChild.required<
        TemplateRef<QpxOverflowTriggerTemplateContext>
    >('defaultTriggerTemplate');

    protected readonly inlineItemTemplate = contentChild(
        QpxOverflowInlineItemTemplate
    );
    protected readonly menuItemTemplate = contentChild(
        QpxOverflowMenuItemTemplate
    );
    protected readonly triggerTemplate = contentChild(
        QpxOverflowTriggerTemplate
    );
    //#endregion

    //#region State
    private readonly overflowedItemIds = signal<readonly string[]>([]);

    protected readonly visibleItems = computed(() => {
        const overflowed = new Set(this.overflowedItemIds());

        return this.items().filter((item) => !overflowed.has(item.id));
    });

    protected readonly overflowItems = computed(() => {
        const overflowed = new Set(this.overflowedItemIds());

        return this.items().filter((item) => overflowed.has(item.id));
    });

    protected readonly hasOverflow = computed(() => {
        return this.overflowItems().length > 0;
    });

    protected readonly resolvedInlineItemTemplate = computed(() => {
        return (
            this.inlineItemTemplate()?.templateRef ??
            this.defaultInlineItemTemplate()
        );
    });

    protected readonly resolvedMenuItemTemplate = computed(() => {
        return (
            this.menuItemTemplate()?.templateRef ??
            this.defaultMenuItemTemplate()
        );
    });

    protected readonly resolvedTriggerTemplate = computed(() => {
        return (
            this.triggerTemplate()?.templateRef ?? this.defaultTriggerTemplate()
        );
    });

    private resizeFrameId: number | null = null;
    //#endregion

    constructor() {
        effect(() => {
            this.items();
            this.inlineItemTemplate();
            this.menuItemTemplate();
            this.triggerTemplate();
            this.scheduleOverflowComputation();
        });

        afterNextRender({
            read: () => {
                const container = this.containerRef()?.nativeElement;
                if (!container) {
                    return;
                }

                const observer = new ResizeObserver(() => {
                    this.scheduleOverflowComputation();
                });

                observer.observe(container);
                this.destroyRef.onDestroy(() => {
                    observer.disconnect();
                });
                this.scheduleOverflowComputation();
            },
        });

        this.destroyRef.onDestroy(() => {
            if (this.resizeFrameId !== null) {
                cancelAnimationFrame(this.resizeFrameId);
            }
        });
    }

    //#region Template methods
    protected getInlineItemContext(
        item: QpxOverflowActionItem,
        index: number
    ): QpxOverflowInlineItemTemplateContext {
        return {
            item,
            index,
            trigger: () => {
                this.activateItem(item);
            },
        };
    }

    protected getMenuItemContext(
        item: QpxOverflowActionItem,
        index: number
    ): QpxOverflowMenuItemTemplateContext {
        return {
            item,
            index,
            trigger: () => {
                this.activateItem(item);
            },
            closeMenu: () => {
                this.closeMenu();
            },
        };
    }

    protected getTriggerContext(): QpxOverflowTriggerTemplateContext {
        return {
            hasOverflow: this.hasOverflow(),
            overflowCount: this.overflowItems().length,
        };
    }
    //#endregion

    //#region Private methods
    private activateItem(item: QpxOverflowActionItem): void {
        if (item.disabled) {
            return;
        }

        item.onSelect?.();
        this.closeMenu();
    }

    private closeMenu(): void {
        this.menuTrigger()?.closeMenu();
    }

    private scheduleOverflowComputation(): void {
        if (this.resizeFrameId !== null) {
            return;
        }

        this.resizeFrameId = requestAnimationFrame(() => {
            this.resizeFrameId = null;
            this.computeOverflow();
        });
    }

    private computeOverflow(): void {
        const container = this.containerRef().nativeElement;
        const measurementRow = this.measurementRowRef().nativeElement;
        const items = this.items();

        if (items.length === 0) {
            this.overflowedItemIds.set([]);
            return;
        }

        const containerWidth = container.clientWidth;
        if (containerWidth <= 0) {
            this.overflowedItemIds.set([]);
            return;
        }

        const gap = this.readGap(measurementRow);
        const itemWidths = new Map<string, number>();

        for (const element of Array.from(
            measurementRow.querySelectorAll<HTMLElement>(
                '[data-measure-item-id]'
            )
        )) {
            const itemId = element.dataset['measureItemId'];

            if (!itemId) {
                continue;
            }

            itemWidths.set(itemId, Math.ceil(element.offsetWidth));
        }

        const triggerWidth = Math.ceil(
            measurementRow.querySelector<HTMLElement>('[data-measure-trigger]')
                ?.offsetWidth ?? 0
        );

        const allItemsWidth = this.getTotalWidth(items, itemWidths, gap);
        if (allItemsWidth <= containerWidth) {
            this.overflowedItemIds.set([]);
            return;
        }

        const visibleItems = [...items];
        const overflowedItemIds: string[] = [];

        while (
            visibleItems.length > 0 &&
            !this.fitsWithTrigger(
                visibleItems,
                itemWidths,
                gap,
                triggerWidth,
                containerWidth
            )
        ) {
            const removalIndex = this.findRemovalIndex(visibleItems);
            const [removedItem] = visibleItems.splice(removalIndex, 1);

            overflowedItemIds.unshift(removedItem.id);
        }

        this.overflowedItemIds.set(overflowedItemIds);
    }

    private findRemovalIndex(items: readonly QpxOverflowActionItem[]): number {
        for (let index = items.length - 1; index >= 0; index--) {
            if (!hasPinnedPriority(items[index])) {
                return index;
            }
        }

        return items.length - 1;
    }

    private fitsWithTrigger(
        items: readonly QpxOverflowActionItem[],
        itemWidths: ReadonlyMap<string, number>,
        gap: number,
        triggerWidth: number,
        containerWidth: number
    ): boolean {
        const itemsWidth = this.getTotalWidth(items, itemWidths, gap);
        const triggerGap = items.length > 0 ? gap : 0;

        return itemsWidth + triggerGap + triggerWidth <= containerWidth;
    }

    private getTotalWidth(
        items: readonly QpxOverflowActionItem[],
        itemWidths: ReadonlyMap<string, number>,
        gap: number
    ): number {
        if (items.length === 0) {
            return 0;
        }

        const widths = items.reduce((total, item) => {
            return total + (itemWidths.get(item.id) ?? 0);
        }, 0);

        return widths + gap * Math.max(items.length - 1, 0);
    }

    private readGap(element: HTMLElement): number {
        const styles = getComputedStyle(element);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');

        return Number.isFinite(gap) ? gap : 0;
    }
    //#endregion
}
